import _, {isUndefined} from 'lodash';
import {createSelector} from 'reselect';

import {buildTeammateDisplayName, isBotTeammate} from '../../../src/helpers/modelHelpers';
import {createVisualContactModel, isContactACustomChannel} from '../../../src/helpers/models/contactHelpers';
import {sortTrackersForConversation} from '../../../src/helpers/models/conversationHelpers';
import {globalNamespace} from '../../../src/helpers/models/namespaceHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../src/helpers/reselectHelpers';
import {ActivityVisualTypesEnum} from '../../../src/models/api/activityModel';
import {CardModel} from '../../../src/models/api/cardModel';
import {ChannelModel} from '../../../src/models/api/channelModel';
import {
  buildContactFromContactUid,
  buildContactUid,
  ContactModel,
  ContactSourcesEnum,
} from '../../../src/models/api/contactModel';
import {MessageTypesEnum} from '../../../src/models/api/messageModel';
import {RecipientModel, RecipientRolesEnum} from '../../../src/models/api/recipientModel';
import {TeammateModel} from '../../../src/models/api/teammateModel';
import {VisualContactModel} from '../../../src/models/api/visualContactModel';
import {AppState} from '../../../src/store/appState';
import {ResourceMap} from '../../../src/store/appTypes';
import {getFlatChannels} from '../../../src/store/data/channels/channelsSelectors';
import {
  getActivities,
  getCards,
  getComments,
  getContacts,
  getConversations,
} from '../../../src/store/data/dataSelectors';
import {getFindTeammate} from '../../../src/store/data/teammates/lazyTeammatesSelectors';
import {getOnlineTeammateIdsSet} from '../../../src/store/data/teammates/onlineTeammatesSelectors';
import {getFlatTeammates, getTeammates, getUser} from '../../../src/store/data/teammates/teammatesSelectors';
import {makeGetContactDetailsPanel} from '../../../src/store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelLocationsEnum} from '../../../src/store/panels/contacts/contactDetailsPanelState';
import {ContactDetailsPanelId} from '../../../src/store/panels/panelStateBase';
import {ViewerPanelPageTypesEnum} from '../../../src/store/panels/viewer/viewerPanelState';
import {FrameViewerState} from '../side/sideFrameActions';

/*
 * Types.
 */

export interface ContactDetailsContainerBaseProps {
  panelId: ContactDetailsPanelId;
  viewerState?: FrameViewerState;
  popupConversationId?: number;
  location: ContactDetailsPanelLocationsEnum;
}

interface CreateVisualContactModelFromContactOptions {
  contact: ContactModel;
  namespace: string;
  allCards?: ResourceMap<CardModel>;
  teammates?: ReadonlyArray<TeammateModel>;
  onlineTeammateIds?: ReadonlySet<number>;
  channels?: ReadonlyArray<ChannelModel>;
  shouldAllowContactsWithoutUrls?: boolean;
}

/*
 * Selectors.
 */

function getConversation(_state: AppState, props: ContactDetailsContainerBaseProps) {
  return props.viewerState?.conversation;
}

function getConversationRecipient(_state: AppState, props: ContactDetailsContainerBaseProps) {
  return props.viewerState?.conversation.recipient;
}

function getConversationNamespace(_state: AppState, props: ContactDetailsContainerBaseProps) {
  return props.viewerState?.conversation.namespace;
}

function getViewerPageActivities(_state: AppState, props: ContactDetailsContainerBaseProps) {
  const page = props.viewerState?.viewerPage;
  return page?.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ? page.activities : undefined;
}

function getViewerStateMessages(_state: AppState, props: ContactDetailsContainerBaseProps) {
  return props.viewerState?.messages;
}

function getPopupConversationId(_state: AppState, props: ContactDetailsContainerBaseProps) {
  return props.popupConversationId;
}

function makeGetConversationContact() {
  return createSelector(
    [
      getConversationRecipient,
      getConversationNamespace,
      getContacts,
      getCards,
      getFlatChannels,
      getFlatTeammates,
      getOnlineTeammateIdsSet,
    ],
    (conversationRecipient, namespace, contacts, cards, channels, teammates, onlineTeammateIds) => {
      if (!conversationRecipient) {
        return null;
      }

      if (!namespace) {
        return null;
      }

      return createVisualContactModelFromRecipient(
        conversationRecipient,
        namespace,
        cards,
        contacts,
        teammates,
        onlineTeammateIds,
        channels,
      );
    },
  );
}

function makeGetConversationParticipants() {
  return createSelector(
    [getConversation, getContacts, getCards, getTeammates, getFlatChannels, getOnlineTeammateIdsSet],
    (conversation, contacts, cards, teammates, channels, onlineTeammateIds) => {
      if (!conversation) {
        return [];
      }
      if (conversation.messageType !== MessageTypesEnum.INTERNAL) {
        return [];
      }

      return _(sortTrackersForConversation(conversation))
        .map((tracker) => {
          const teammate = teammates[tracker.teammateId];
          if (!teammate) {
            return undefined;
          }

          return createVisualContactModelFromTeammate(
            teammate,
            conversation.namespace,
            cards,
            contacts,
            onlineTeammateIds,
            channels,
          );
        })
        .value();
    },
  );
}

function makeGetCommentContacts() {
  return wrapInShallowSelector(
    createSelector(
      [
        getViewerPageActivities,
        getConversationNamespace,
        getActivities,
        getComments,
        getContacts,
        getCards,
        getFindTeammate,
        getFlatChannels,
        getOnlineTeammateIdsSet,
      ],
      (
        viewerPageActivities,
        namespace,
        activities,
        comments,
        contacts,
        cards,
        findTeammate,
        channels,
        onlineTeammateIds,
      ) => {
        if (!namespace) {
          return [];
        }

        return _(viewerPageActivities)
          .values()
          .map((pageActivity) => {
            if (!pageActivity) {
              return null;
            }

            const activity = activities[pageActivity.activityId];
            if (!activity) {
              return null;
            }

            if (activity.visualType !== ActivityVisualTypesEnum.COMMENT) {
              return null;
            }

            const comment = comments[activity.commentUid];
            if (!comment) {
              return null;
            }

            const teammate = findTeammate(comment.authorId, 'ContactDetailsSelector makeGetCommentContacts');
            if (!teammate) {
              return null;
            }

            // Bot teammates don't have contact details, should not be shown
            if (isBotTeammate(teammate)) {
              return null;
            }

            return createVisualContactModelFromTeammate(
              teammate,
              namespace,
              cards,
              contacts,
              onlineTeammateIds,
              channels,
            );
          })
          .compact()
          .value();
      },
    ),
  );
}

function makeGetMessageContacts() {
  return createSelector(
    [
      getViewerStateMessages,
      getConversationNamespace,
      getContacts,
      getCards,
      getFlatChannels,
      getFlatTeammates,
      getOnlineTeammateIdsSet,
    ],
    (messages, namespace, contacts, cards, channels, teammates, onlineTeammateIds) => {
      if (!namespace) {
        return [];
      }

      return _(messages)
        .map((m) => m.recipients)
        .flatten()
        .uniqBy((rp) => rp.contactUid)
        .filter((rp) =>
          [
            RecipientRolesEnum.TO,
            RecipientRolesEnum.CC,
            RecipientRolesEnum.FROM,
            RecipientRolesEnum.REPLY_TO,
          ].includes(rp.role),
        )
        .map((rp) =>
          createVisualContactModelFromRecipient(
            rp,
            namespace,
            cards,
            contacts,
            teammates,
            onlineTeammateIds,
            channels,
          ),
        )
        .value();
    },
  );
}

export function makeGetPopupConversation() {
  return createSelector([getPopupConversationId, getConversations], (conversationId, conversations) =>
    isUndefined(conversationId) ? undefined : conversations[conversationId],
  );
}

export function makeGetPopoutPanelSelectedContact(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
  getPopupConversation: ReturnType<typeof makeGetPopupConversation>,
) {
  return createSelector(
    [
      getContactDetailsPanel,
      getPopupConversation,
      getContacts,
      getCards,
      getFlatTeammates,
      getFlatChannels,
      getOnlineTeammateIdsSet,
    ],
    (panel, conversation, contacts, cards, teammates, channels, onlineTeammateIds) => {
      if (!panel || !panel.popupAppPanelId) {
        return undefined;
      }

      // Check if we should try and create the contact from not a conversation.
      if (
        !conversation ||
        !conversation.recipient ||
        conversation.recipient.contactUid !== panel.selectedContactUid
      ) {
        const contact = findContactForPopout(contacts, cards, panel.selectedContactUid, panel.selectedCardId);
        if (contact) {
          return createVisualContactModelFromContact({
            contact,
            namespace: panel.popupContactNamespace ?? conversation?.namespace ?? globalNamespace,
            allCards: cards,
            teammates,
            onlineTeammateIds,
            channels,
          });
        }

        const teammate =
          panel.selectedContactUid && teammates.find((tm) => tm.contactUid === panel.selectedContactUid);
        if (teammate) {
          return createVisualContactModelFromTeammate(
            teammate,
            globalNamespace,
            cards,
            contacts,
            onlineTeammateIds,
            channels,
          );
        }
        return null;
      }

      return createVisualContactModelFromRecipient(
        conversation.recipient,
        panel.popupContactNamespace ?? conversation.namespace,
        cards,
        contacts,
        teammates,
        onlineTeammateIds,
        channels,
      );
    },
  );
}

export function makeGetContactsManagerPanelSelectedCardContact(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
) {
  return createSelector(
    [
      getContactDetailsPanel,
      getContacts,
      getCards,
      getFlatTeammates,
      getFlatChannels,
      getOnlineTeammateIdsSet,
    ],
    (panel, contacts, cards, teammates, channels, onlineTeammateIds) => {
      if (
        !panel ||
        !panel.selectedCardId ||
        panel.location !== ContactDetailsPanelLocationsEnum.CONTACTS_MANAGER
      ) {
        return undefined;
      }

      const card = cards[panel.selectedCardId];
      if (!card || card.contactUids.length === 0) {
        return null;
      }
      // Select the first contact uid
      const contact = contacts[card.contactUids[0]];
      if (!contact) {
        return null;
      }
      return createVisualContactModelFromContact({
        contact,
        namespace: card.namespace,
        allCards: cards,
        teammates,
        onlineTeammateIds,
        channels,
      });
    },
  );
}
export type VisualContactsSelector<P> = AppParametricSelector<P, ReadonlyArray<VisualContactModel>>;
export function makeGetVisualContacts<P extends ContactDetailsContainerBaseProps>(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
): VisualContactsSelector<P> {
  const getConversationContact = makeGetConversationContact();
  const getMessageContacts = makeGetMessageContacts();
  const getCommentContacts = makeGetCommentContacts();
  const getConversationParticipants = makeGetConversationParticipants();

  return createSelector(
    [
      getConversationContact,
      getMessageContacts,
      getCommentContacts,
      getConversationParticipants,
      getUser,
      getContactDetailsPanel,
    ],
    (conversationContact, messageContacts, commentContacts, conversationParticipants, user, panel) => {
      const userContactUid = buildContactUid(ContactSourcesEnum.EMAIL, user.email);

      const contacts = _([
        conversationContact,
        ...messageContacts,
        ...commentContacts,
        ...conversationParticipants,
      ])
        .compact()
        .filter((c) => c.contact.uid !== userContactUid)
        .uniqBy((c) => c.contact.uid)
        .value();

      // If we failed to find any contacts, we may be loaded from a source like Contact Card.
      if (contacts.length === 0 && panel?.selectedContactUid) {
        const createdContact = createVisualContactModelFromContact({
          contact: buildContactFromContactUid(panel.selectedContactUid),
          namespace: panel.popupContactNamespace ?? globalNamespace,
          shouldAllowContactsWithoutUrls: true,
        });
        if (createdContact) {
          return [createdContact];
        }
      }

      return contacts;
    },
  );
}

export function makeGetSelectedVisualContact<P>(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
  getVisualContacts: VisualContactsSelector<P>,
) {
  return createSelector([getContactDetailsPanel, getVisualContacts], (panel, visualContacts) => {
    const contactUid = panel?.selectedContactUid;
    return visualContacts.find((visualContact) => visualContact?.contact.uid === contactUid);
  });
}

export function makeGetSelectedCard(getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>) {
  return createSelector([getContactDetailsPanel, getCards], (panel, cards) => {
    const cardId = panel?.selectedCardId;
    return cardId ? cards[cardId] : undefined;
  });
}

/*
 * Helpers.
 */

function findContactForPopout(
  contacts: ResourceMap<ContactModel>,
  cards: ResourceMap<CardModel>,
  selectedContactUid?: string,
  selectedCardId?: number,
) {
  if (selectedContactUid) {
    return contacts[selectedContactUid];
  }

  if (selectedCardId) {
    const card = cards[selectedCardId];
    if (!card) {
      return undefined;
    }
    return _(contacts)
      .values()
      .compact()
      .find((contact) => contact.cardIds[card.namespace] === card.id);
  }

  return undefined;
}

function createVisualContactModelFromRecipient(
  recipient: RecipientModel,
  namespace: string,
  allCards: ResourceMap<CardModel>,
  allContacts: ResourceMap<ContactModel>,
  teammates: ReadonlyArray<TeammateModel>,
  onlineTeammateIds: ReadonlySet<number> | undefined,
  channels: ReadonlyArray<ChannelModel>,
) {
  const contact = allContacts[recipient.contactUid];
  if (!contact) {
    return null;
  }

  const teammate = teammates && teammates.find((t) => t.email === contact.handle);
  return createVisualContactModel({
    contact,
    namespace,
    allCards,
    name: buildDisplayName(recipient, undefined, contact, channels),
    teammate,
    onlineTeammateIds,
  });
}

function createVisualContactModelFromTeammate(
  teammate: TeammateModel,
  namespace: string,
  allCards: ResourceMap<CardModel>,
  allContacts: ResourceMap<ContactModel>,
  onlineTeammateIds: ReadonlySet<number> | undefined,
  channels: ReadonlyArray<ChannelModel>,
) {
  const contactUid = buildContactUid(ContactSourcesEnum.EMAIL, teammate.email);
  const contact = allContacts[contactUid];

  return createVisualContactModel({
    contact: !contact ? buildContactFromContactUid(contactUid) : contact,
    namespace,
    allCards,
    name: buildDisplayName(undefined, teammate, contact, channels),
    fallbackAvatarUrl: teammate.avatarUrl,
    teammate,
    onlineTeammateIds,
    shouldAllowContactsWithoutUrls: true,
  });
}

function createVisualContactModelFromContact(options: CreateVisualContactModelFromContactOptions) {
  const {
    contact,
    namespace,
    allCards = {},
    teammates = [],
    onlineTeammateIds,
    channels = [],
    shouldAllowContactsWithoutUrls = false,
  } = options;
  const teammate = teammates.find((tm) => tm.email === contact.handle);
  return createVisualContactModel({
    contact,
    namespace,
    allCards,
    name: buildDisplayName(undefined, teammate, contact, channels),
    teammate,
    onlineTeammateIds,
    shouldAllowContactsWithoutUrls,
  });
}

function buildDisplayName(
  recipient: RecipientModel | undefined,
  teammate: TeammateModel | undefined,
  contact: ContactModel | undefined,
  channels: ReadonlyArray<ChannelModel>,
) {
  // If we have a contact, check if the contact is a custom channel
  if (contact && isContactACustomChannel(contact)) {
    const channel = channels.find((c) => c.id === contact.channelId);
    if (channel) {
      return channel.displayName;
    }
  }
  // If we have a recipient, use that name
  if (recipient) {
    return recipient.name;
  }
  // If we have a teammate, create the name
  if (teammate) {
    return buildTeammateDisplayName(teammate);
  }
  return undefined;
}
